import React from "react";
import { Reveal } from "@/components/Motion";
import CtaBanner from "@/components/CtaBanner";
import Seo from "@/components/Seo";
import { FAQS } from "@/lib/site-data";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function FaqPage() {
  return (
    <div data-testid="page-faq">
      <Seo
        title="FAQ | Cutting Edge Technologies Consulting — Houston TX & Washington DC"
        description="Answers about our IT Consulting, Training, Managed Services, and Staffing — industries served, remote training, how we work, and how to get started."
        path="/faq"
      />

      {/* JSON-LD FAQPage: enables rich snippets and AI citation */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
      <section className="relative bg-slate-950 text-white pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-lines-dark opacity-50" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/10 blur-3xl rounded-full" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal><div className="eyebrow text-emerald-400">FAQ</div></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-5xl">
              Answers to the questions we hear most.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-lg md:text-xl text-slate-300 leading-relaxed">
              Don't see yours? Reach out and we'll get back to you within one business day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="mx-auto max-w-[1000px] px-6 md:px-12 lg:px-16">
          <Accordion type="single" collapsible className="divide-y divide-slate-200 border-y border-slate-200">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`} className="border-b-0" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="py-6 text-left font-display text-xl md:text-2xl text-slate-900 tracking-tight hover:no-underline hover:text-emerald-600 data-[state=open]:text-slate-900 [&[data-state=open]>svg]:text-emerald-600">
                  <span className="flex items-start gap-5">
                    <span className="font-mono text-xs text-emerald-600 tracking-widest pt-2">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{f.q}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-6 pl-12 text-base text-slate-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CtaBanner
        eyebrow="Still have questions?"
        title="We'd rather talk it through."
        body="30-minute consults are on the house. No sales pitch, just a real conversation."
      />
    </div>
  );
}
